/*
 * Owner Booking Pace dashboard — owner-facing fork of the admin page.
 *
 * Traceability: FR-/EC-/S- IDs reference
 * .verified/features/owner-facing-analytics-dashboards/spec.md (ADR-025, the
 * owner dashboard fork). The layout-editor desktop gate is FR-23
 * (dashboard-layout-editor, ADR-022).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/lib/shadcn/ui/card";
import { Input } from "@/lib/shadcn/ui/input";
import { Alert, AlertDescription } from "@/lib/shadcn/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/lib/shadcn/ui/accordion";
import { Loader2 } from "lucide-react";
import { SearchableSelect } from "@/lib/shadcn/components/searchable-select";
import { dateToString } from "@/utils/periods";
import { useAppDispatch, useAppSelector } from "@/store/store";
import {
    setEndDate,
    setPreviousEndDate,
    setPreviousStartDate,
    setPreviousYearEndDate,
    setPreviousYearStartDate,
    setStartDate,
    updateStats,
} from "@/store/bookingPaceSlice";
import { useGetOwnerBookingPaceQuery } from "@/store/booking-management-api";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { useActiveApartments, useActiveApartmentOptions } from "@/store/slices/apartments";
import { operationalPropertyCount } from "@/utils/operationalProperties";
import DashboardGrid from "@/lib/dashboard-grid/DashboardGrid";
import DashboardToolbar from "@/lib/dashboard-grid/DashboardToolbar";
import { useDashboardLayout } from "@/lib/dashboard-grid/useDashboardLayout";
import type { DashboardView, LayoutItem } from "@/lib/dashboard-grid/types";
import { apiWindowDay, buildBookingPaceTiles, inclusivePeriodDays, sectionIdForKey, type SectionStats } from "@/pages/dashboards/booking-pace-tiles";

const DASHBOARD_ID = "ownerBookingPace";

/**
 * Desktop gate (FR-23): the layout editor is offered only at `lg`+ (1024px).
 * Subscribes to matchMedia so a resize across the breakpoint flips the gate live.
 */
function useIsDesktop(): boolean {
    const query = "(min-width: 1024px)";
    const [isDesktop, setIsDesktop] = useState<boolean>(() => {
        if (typeof window === "undefined" || !window.matchMedia) return true;
        return window.matchMedia(query).matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia(query);
        const onChange = () => setIsDesktop(mq.matches);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsDesktop(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    return isDesktop;
}

/**
 * Owner-facing fork of the admin `booking-pace.tsx`. Owners scope by owner + a
 * single apartment — no group selector, no shared `ApartmentFilterBar`. The owner
 * selector shows only for multi-owner users; a single-owner user is pinned. Pace
 * data comes from the owner-scoped `useGetOwnerBookingPaceQuery` (server-side
 * `:ownerId` scoping). The date-range slice and `buildBookingPaceTiles` are reused.
 */
const OwnerBookingPacePage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { ownerOptions, apartmentDetailsMap, selectedOwner } = useAppData();
    const { setSelectedOwner } = useUserSettingsDispatch();
    const activeApartments = useActiveApartments();
    const activeApartmentOptions = useActiveApartmentOptions();
    const isDesktop = useIsDesktop();

    // Active owner lives in the shared `userSettings` slice (persisted, remembered
    // across owner-scoped pages). Fall back to the first visible owner while the
    // slice settles `selectedOwner` (starts at 0) so the pace query can fire on
    // first paint without waiting a render.
    const ownerId = selectedOwner !== 0 ? selectedOwner : ownerOptions.length > 0 ? Number(ownerOptions[0].value) : 0;
    const [selectedApartment, setSelectedApartment] = useState<number>(0);

    useEffect(() => {
        if (selectedOwner === 0 && ownerOptions.length > 0) {
            setSelectedOwner(Number(ownerOptions[0].value));
        }
    }, [ownerOptions, selectedOwner, setSelectedOwner]);

    // EC-003: switching owner clears a now-out-of-scope apartment selection.
    useEffect(() => {
        if (selectedApartment === 0) {
            return;
        }
        const apartment = apartmentDetailsMap.get(selectedApartment);
        if (!apartment || !apartment.ownerIds.includes(ownerId)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedApartment(0);
        }
    }, [ownerId, selectedApartment, apartmentDetailsMap]);

    // Apartment options scoped to the selected owner (keep the "All" sentinel).
    const apartmentOptions = useMemo(
        () =>
            activeApartmentOptions.filter((opt) => {
                if (opt.value === "0") {
                    return true;
                }
                const apartment = apartmentDetailsMap.get(Number(opt.value));
                return apartment?.ownerIds.includes(ownerId) ?? false;
            }),
        [activeApartmentOptions, apartmentDetailsMap, ownerId],
    );

    const ownedApartments = useMemo(
        () => activeApartments.filter((apt) => apt.ownerIds.includes(ownerId)),
        [activeApartments, ownerId],
    );

    const {
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        previousYearStartDate,
        previousYearEndDate,
        bookingPaceStats,
        bookingPaceStatsPrevious,
        bookingPaceStatsPreviousYear,
    } = useAppSelector((state) => state.bookingPace);

    // Owner-scoped pace trio (current / previous / previous-year). Skipped until
    // an owner id resolves so we never fire `/owners/0/pace`.
    const skip = ownerId === 0;

    const {
        data: currentData,
        isLoading: isLoadingCurrent,
        error: errorCurrent,
    } = useGetOwnerBookingPaceQuery(
        { ownerId, startDate: apiWindowDay(startDate), endDate: apiWindowDay(endDate) },
        { skip },
    );

    const {
        data: previousData,
        isLoading: isLoadingPrevious,
        error: errorPrevious,
    } = useGetOwnerBookingPaceQuery(
        {
            ownerId,
            startDate: apiWindowDay(previousStartDate),
            endDate: apiWindowDay(previousEndDate),
        },
        { skip },
    );

    const {
        data: previousYearData,
        isLoading: isLoadingPreviousYear,
        error: errorPreviousYear,
    } = useGetOwnerBookingPaceQuery(
        {
            ownerId,
            startDate: apiWindowDay(previousYearStartDate),
            endDate: apiWindowDay(previousYearEndDate),
        },
        { skip },
    );

    // Fold each window's payload into the reducer, scoped to the selected
    // apartment (groupId is not applicable to owner scope → 0).
    useEffect(() => {
        if (currentData) {
            dispatch(updateStats({ data: currentData, type: "current", apartmentId: selectedApartment, groupId: 0 }));
        }
    }, [currentData, selectedApartment, dispatch]);

    useEffect(() => {
        if (previousData) {
            dispatch(updateStats({ data: previousData, type: "previous", apartmentId: selectedApartment, groupId: 0 }));
        }
    }, [previousData, selectedApartment, dispatch]);

    useEffect(() => {
        if (previousYearData) {
            dispatch(
                updateStats({ data: previousYearData, type: "previousYear", apartmentId: selectedApartment, groupId: 0 }),
            );
        }
    }, [previousYearData, selectedApartment, dispatch]);

    const isLoading = skip || isLoadingCurrent || isLoadingPrevious || isLoadingPreviousYear;
    const error = errorCurrent || errorPrevious || errorPreviousYear;
    // One day count per column, each from its OWN window: the comparison windows are
    // independently editable, so a previous-period numerator over the current window's
    // length would rescale the comparison by the difference in window lengths.
    const numberOfDays = inclusivePeriodDays(startDate, endDate);
    const numberOfDaysPrev = inclusivePeriodDays(previousStartDate, previousEndDate);
    const numberOfDaysPrevYear = inclusivePeriodDays(previousYearStartDate, previousYearEndDate);

    // Empty state is data-driven, not size-driven: `updateStats` always seeds an
    // "All Sources" bucket, so an empty payload still yields `size === 1`. Detect
    // "no data" from the raw current-window records (after the apartment filter).
    const currentRecords = useMemo(() => {
        const records = currentData ?? [];
        return selectedApartment !== 0 ? records.filter((r) => r.apartmentId === selectedApartment) : records;
    }, [currentData, selectedApartment]);
    const hasData = currentRecords.length > 0;

    // Bookings-Per-Property denominator: the owner's apartments surviving the same
    // apartment selection, counted per window against that window's own dates.
    const filteredApartments = ownedApartments.filter((apt) => (selectedApartment !== 0 ? apt.id === selectedApartment : true));

    const opCountCurrent = operationalPropertyCount(filteredApartments, startDate, endDate);
    const opCountPrev = operationalPropertyCount(filteredApartments, previousStartDate, previousEndDate);
    const opCountPrevYear = operationalPropertyCount(filteredApartments, previousYearStartDate, previousYearEndDate);

    const period = `${dateToString(startDate)} - ${dateToString(endDate)}`;

    const sectionsData: SectionStats[] = useMemo(() => {
        return Array.from(bookingPaceStats.entries())
            .sort(([, a], [, b]) => b.grossIncome - a.grossIncome)
            .map(([key]) => ({
                key,
                stats: bookingPaceStats.get(key),
                prev: bookingPaceStatsPrevious.get(key),
                prevYear: bookingPaceStatsPreviousYear.get(key),
            }));
    }, [bookingPaceStats, bookingPaceStatsPrevious, bookingPaceStatsPreviousYear]);

    const { sections, registry } = useMemo(
        () =>
            buildBookingPaceTiles({
                sectionsData,
                numberOfDays,
                numberOfDaysPrev,
                numberOfDaysPrevYear,
                period,
                opCountCurrent,
                opCountPrev,
                opCountPrevYear,
                allStats: bookingPaceStats,
            }),
        [sectionsData, numberOfDays, numberOfDaysPrev, numberOfDaysPrevYear, period, opCountCurrent, opCountPrev, opCountPrevYear, bookingPaceStats],
    );

    const layout = useDashboardLayout(DASHBOARD_ID, sections, registry);
    const { working, editMode, setWorking } = layout;

    const onLayoutChange = useCallback(
        (sectionId: string, items: LayoutItem[]) => {
            setWorking({ ...working, sections: { ...working.sections, [sectionId]: items } });
        },
        [working, setWorking],
    );

    const onToggleHide = useCallback(
        (id: string) => {
            const isHidden = working.hidden.includes(id);
            const hidden = isHidden ? working.hidden.filter((h) => h !== id) : [...working.hidden, id];
            setWorking({ ...working, hidden });
        },
        [working, setWorking],
    );

    const onMove = useCallback(
        (id: string, dir: "prev" | "next") => {
            const sectionId = registry.find((t) => t.id === id)?.section;
            if (!sectionId) return;
            const items = [...(working.sections[sectionId] ?? [])];
            const ordered = [...items].sort((a, b) => (a.y ?? 0) - (b.y ?? 0) || (a.x ?? 0) - (b.x ?? 0));
            const idx = ordered.findIndex((it) => it.i === id);
            if (idx < 0) return;
            const swapWith = dir === "prev" ? idx - 1 : idx + 1;
            if (swapWith < 0 || swapWith >= ordered.length) return;
            const a = ordered[idx];
            const b = ordered[swapWith];
            const swapped = items.map((it) => {
                if (it.i === a.i) return { ...it, x: b.x, y: b.y };
                if (it.i === b.i) return { ...it, x: a.x, y: a.y };
                return it;
            });
            setWorking({ ...working, sections: { ...working.sections, [sectionId]: swapped } });
        },
        [working, setWorking, registry],
    );

    useEffect(() => {
        document.title = "Booking Pace";
    }, []);

    const sectionById = useMemo(() => new Map(sections.map((s) => [s.id, s])), [sections]);

    const renderSectionGrid = (sectionId: string): React.ReactNode => {
        const section = sectionById.get(sectionId);
        if (!section) return null;
        const scopedView: DashboardView = working;
        return (
            <DashboardGrid
                sections={[section]}
                registry={registry}
                view={scopedView}
                editMode={editMode}
                onLayoutChange={onLayoutChange}
                onToggleHide={onToggleHide}
                onMove={onMove}
                renderCtx={{}}
            />
        );
    };

    return (
        <div className="space-y-6" data-testid="owner-booking-pace-page">
            {/* Filters */}
            <div role="group" aria-label="Filters" className="flex justify-end gap-4">
                {/* Owner select — only for multi-owner users. */}
                {ownerOptions.length > 1 && (
                    <div className="w-[200px]" data-testid="owner-booking-pace-owner-select">
                        <SearchableSelect
                            variant="volt"
                            selectedValue={ownerId.toString()}
                            onChange={(value) => setSelectedOwner(Number(value))}
                            placeholder="Select Owner"
                            options={ownerOptions}
                        />
                    </div>
                )}

                {/* Apartment Select — scoped to the selected owner. */}
                <div className="w-auto">
                    <SearchableSelect
                        variant="volt"
                        selectedValue={selectedApartment.toString()}
                        onChange={(value) => setSelectedApartment(Number(value))}
                        placeholder="Select Apartment"
                        options={apartmentOptions}
                        selectedValuePrefix="Apartment"
                        width={"w-full"}
                    />
                </div>
            </div>

            <h2 className="text-2xl font-bold">Booking Pace</h2>

            {/* Dashboard layout editor toolbar (FR-23: desktop-gated). */}
            <div className="flex flex-col gap-1">
                <DashboardToolbar
                    views={layout.views}
                    activeViewId={layout.activeViewId}
                    editMode={editMode}
                    modified={layout.modified}
                    onEnterEdit={layout.enterEdit}
                    onSave={layout.save}
                    onSaveAs={layout.saveAs}
                    onReset={layout.reset}
                    onDelete={layout.del}
                    onCancel={layout.cancel}
                    onSwitchView={layout.switchView}
                    canEdit={isDesktop}
                />
                {!isDesktop && (
                    <span className="text-sm text-muted-foreground" data-testid="edit-desktop-only-hint">
                        Editing available on a larger screen.
                    </span>
                )}
                {editMode && (
                    <p className="text-sm text-muted-foreground" data-testid="edit-instruction-line">
                        Drag the grip to reorder within a section · drag the right edge to resize · eye to hide. Changes aren&apos;t saved
                        until you Save.
                    </p>
                )}
            </div>

            {/* Date Selection Card */}
            <Card className="bg-white">
                <CardContent className="pt-6">
                    {/* Current Period */}
                    <div className="flex justify-between items-center">
                        <span id="pace-current-period-label" className="text-sm text-muted-foreground">
                            Bookings received between:
                        </span>
                        <div role="group" aria-labelledby="pace-current-period-label" className="flex items-center gap-2">
                            <Input
                                type="date"
                                aria-label="Start date"
                                value={apiWindowDay(startDate)}
                                onChange={(e) => dispatch(setStartDate(new Date(e.target.value)))}
                                max={apiWindowDay(endDate)}
                                className="w-auto"
                            />
                            <span aria-hidden="true">-</span>
                            <Input
                                type="date"
                                aria-label="End date"
                                value={apiWindowDay(endDate)}
                                onChange={(e) => dispatch(setEndDate(new Date(e.target.value)))}
                                min={apiWindowDay(startDate)}
                                className="w-auto"
                            />
                        </div>
                    </div>

                    {/* Previous Period */}
                    <div className="flex justify-between items-center">
                        <span id="pace-previous-period-label" className="text-sm text-muted-foreground">
                            vs Previous Period Dates:
                        </span>
                        <div role="group" aria-labelledby="pace-previous-period-label" className="flex items-center gap-2">
                            <Input
                                type="date"
                                aria-label="Start date"
                                value={apiWindowDay(previousStartDate)}
                                onChange={(e) => dispatch(setPreviousStartDate(new Date(e.target.value)))}
                                max={apiWindowDay(previousEndDate)}
                                className="w-auto"
                            />
                            <span aria-hidden="true">-</span>
                            <Input
                                type="date"
                                aria-label="End date"
                                value={apiWindowDay(previousEndDate)}
                                onChange={(e) => dispatch(setPreviousEndDate(new Date(e.target.value)))}
                                min={apiWindowDay(previousStartDate)}
                                className="w-auto"
                            />
                        </div>
                    </div>

                    {/* Previous Year */}
                    <div className="flex justify-between items-center">
                        <span id="pace-previous-year-label" className="text-sm text-muted-foreground">
                            vs Previous Year Dates:
                        </span>
                        <div role="group" aria-labelledby="pace-previous-year-label" className="flex items-center gap-2">
                            <Input
                                type="date"
                                aria-label="Start date"
                                value={apiWindowDay(previousYearStartDate)}
                                onChange={(e) => dispatch(setPreviousYearStartDate(new Date(e.target.value)))}
                                max={apiWindowDay(previousYearEndDate)}
                                className="w-auto"
                            />
                            <span aria-hidden="true">-</span>
                            <Input
                                type="date"
                                aria-label="End date"
                                value={apiWindowDay(previousYearEndDate)}
                                onChange={(e) => dispatch(setPreviousYearEndDate(new Date(e.target.value)))}
                                min={apiWindowDay(previousYearStartDate)}
                                className="w-auto"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Content */}
            {isLoading ? (
                <div role="status" aria-label="Loading booking pace data" className="flex justify-center items-center min-h-[200px]">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="sr-only">Loading…</span>
                </div>
            ) : error ? (
                <Alert variant="destructive">
                    <AlertDescription>{error.toString()}</AlertDescription>
                </Alert>
            ) : !hasData ? (
                <Alert>
                    <AlertDescription>No data available for the selected date range. Please try a different range.</AlertDescription>
                </Alert>
            ) : (
                <Accordion type="multiple" defaultValue={["All Sources"]} className="space-y-4">
                    {sectionsData.map(({ key }) => {
                        const sectionId = sectionIdForKey(key);
                        return (
                            <AccordionItem key={key} value={key} className="border rounded-lg">
                                <AccordionTrigger className="hover:no-underline px-6">
                                    <span className="text-lg font-medium">{key}</span>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <div className="px-6 pb-6">{renderSectionGrid(sectionId)}</div>
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            )}
        </div>
    );
};

export default OwnerBookingPacePage;
